import { RefreshCw, CheckCircle, XCircle, Clock } from "lucide-react";
import { useSyncJobs, useTriggerSync, useSyncHealth } from "@/api/sync";
import { usePagination } from "@/hooks/use-pagination";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/data-table/data-table";
import { useAuth } from "@/stores/auth-store";
import { isSuperAdmin } from "@/lib/roles";
import type { ColumnDef } from "@tanstack/react-table";
import type { SyncJob } from "@/api/types";

const statusIcons: Record<string, React.ElementType> = {
  completed: CheckCircle,
  failed: XCircle,
  running: RefreshCw,
  pending: Clock,
};

const statusColors: Record<string, "success" | "danger" | "warning" | "info"> = {
  completed: "success",
  failed: "danger",
  running: "info",
  pending: "warning",
};

const jobColumns: ColumnDef<SyncJob, unknown>[] = [
  {
    accessorKey: "integration_source",
    header: "Source",
    cell: ({ row }) => <Badge variant="accent">{row.original.integration_source}</Badge>,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <Badge variant={statusColors[row.original.status]}>{row.original.status}</Badge>,
  },
  { accessorKey: "records_created", header: "Created" },
  { accessorKey: "records_updated", header: "Updated" },
  { accessorKey: "errors_count", header: "Errors" },
  {
    accessorKey: "started_at",
    header: "Started",
    cell: ({ row }) => row.original.started_at ? new Date(row.original.started_at).toLocaleString() : "N/A",
  },
  {
    accessorKey: "completed_at",
    header: "Completed",
    cell: ({ row }) => row.original.completed_at ? new Date(row.original.completed_at).toLocaleString() : "N/A",
  },
];

export function SyncManagementPage() {
  const { user } = useAuth();
  const { page, pageSize, params, setPage, setPageSize } = usePagination();
  const { data: health, isLoading: healthLoading } = useSyncHealth();
  const { data: jobs, isLoading: jobsLoading } = useSyncJobs(params);
  const triggerSync = useTriggerSync();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Sync Management</h1>

      <div className="grid gap-4 md:grid-cols-3">
        {["crowdstrike", "atera", "jumpcloud"].map((source) => {
          const h = health?.find((item) => item.integration_source === source);
          const StatusIcon = h?.last_job_status ? statusIcons[h.last_job_status] || Clock : Clock;
          return (
            <Card key={source}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="capitalize">{source}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Last Sync</span>
                    <span>{h?.last_sync_at ? new Date(h.last_sync_at).toLocaleString() : "Never"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Last Status</span>
                    <div className="flex items-center gap-1">
                      <StatusIcon className="h-3 w-3" />
                      <span>{h?.last_job_status || "N/A"}</span>
                    </div>
                  </div>
                  {isSuperAdmin(user) && (
                    <Button
                      size="sm"
                      className="mt-2 w-full"
                      onClick={() => triggerSync.mutate(source)}
                      disabled={triggerSync.isPending}
                    >
                      <RefreshCw className="mr-1 h-3 w-3" /> Sync Now
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader><CardTitle>Job History</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            columns={jobColumns}
            data={jobs?.data ?? []}
            isLoading={jobsLoading}
            page={page}
            pageSize={pageSize}
            totalPages={jobs?.meta?.totalPages ?? 1}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            total={jobs?.meta?.total}
          />
        </CardContent>
      </Card>
    </div>
  );
}
